import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import img42 from "../../../assets/img42.jpg";
import { CheckCircle } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function FirstHomeSavingsAccount() {
  const container = "max-w-[1380px] mx-auto";

  return (
    <>
      <SEO
        title="FHSA Canada | Épargne pour votre première maison – einsured.ca"
        description="Ouvrez un FHSA au Canada. Cotisations déductibles d'impôt et retraits libres d'impôt pour épargner intelligemment en vue de l'achat de votre première maison."
        keywords="FHSA Canada, Compte d'épargne libre d'impôt pour l'achat d'une première propriété, subvention premier acheteur, FHSA vs REER, épargne maison libre d'impôt"
      />
      {/* HERO SECTION */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 md:px-20 text-center md:text-left"
        style={{ backgroundImage: `url(${img42})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>

        <div className={`${container} w-full`}>
          <div className="relative z-10 text-white max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
              Compte d'épargne libre d'impôt pour l'achat d'une première propriété (FHSA)
            </h1>
            <p className="text-base sm:text-xl font-medium mb-6">
              Un chemin plus intelligent vers votre première maison avec des cotisations déductibles d'impôt
              et des retraits libres d'impôt pour les acheteurs canadiens
            </p>
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition shadow-md text-base sm:text-lg"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* WHAT IS FHSA */}
      <section className="py-16 sm:py-20 md:py-20 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className={container}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-8">
            Qu'est-ce qu'un FHSA ?
          </h2>

          <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
            Un{" "}
            <a
              href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/first-home-savings-account.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-600 font-medium underline hover:opacity-90"
            >
              Compte d'épargne libre d'impôt pour l'achat d'une première propriété (FHSA)
            </a>{" "}
            est un compte d'épargne exonéré d'impôt créé spécifiquement pour aider
            les futurs propriétaires au Canada à épargner en vue de l'acquisition d'une
            première habitation admissible.
          </p>

          <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
            En combinant les avantages d'un{" "}
            <Link
              to="/fr/regime-epargne-etudes"
              className="text-sky-600 font-medium underline hover:opacity-90"
            >
              REER
            </Link>{" "}
            et d'un{" "}
            <Link
              to="/fr/compte-epargne-libre-impot"
              className="text-sky-600 font-medium underline hover:opacity-90"
            >
              CELI
            </Link>
            , le FHSA offre une déduction qui réduit votre revenu imposable annuel
            tout en vous permettant de réaliser des rendements à l'abri de l'impôt.
          </p>

          <p className="text-base sm:text-lg leading-7 sm:leading-8">
            Par la suite, les fonds accumulés peuvent être utilisés pour financer l'achat
            d'une première maison, et les retraits sont exonérés d'impôt.
            De plus, il n'y a aucune obligation de rembourser les montants retirés
            du FHSA.
          </p>
        </div>
      </section>

      {/* Why is contributing to a First Home Savings */}
      <section
        className="relative py-16 sm:py-20 md:py-24 px-4 sm:px-6 md:px-24 bg-cover bg-center lg:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className={`relative z-10 ${container} text-center text-white`}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
            Pourquoi est-il nécessaire de cotiser à un compte d'épargne pour l'achat d'une première propriété ?
          </h2>

          <p className="text-base sm:text-lg leading-relaxed text-gray-100 max-w-4xl mx-auto mb-6 text-left sm:text-center">
            Cotiser à un{" "}
            <span
              rel="noopener noreferrer"
              className="text-sky-400 font-medium hover:opacity-90"
            >
              Compte d'épargne pour l'achat d'une première propriété (FHSA)
            </span>{" "}
            ou à un programme similaire peut être nécessaire pour plusieurs raisons,
            selon les caractéristiques et les incitatifs spécifiques offerts par le programme.
          </p>

          <p className="text-lg sm:text-xl font-semibold text-gray-100 mb-12 px-2">
            Voici les raisons courantes pour lesquelles cotiser à un compte d'épargne pour
            l'achat d'une première propriété peut être nécessaire ou bénéfique :
          </p>

          {/* GRID CONTAINER */}
          <div className="grid md:grid-cols-2 gap-8 text-left">
            {/* 1 */}
            <div className="bg-white text-gray-800 p-6 sm:p-8 rounded-3xl shadow-md">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Incitatifs gouvernementaux :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Le programme FHSA est assorti d'incitatifs gouvernementaux, tels que des
                avantages fiscaux, des subventions ou des cotisations de contrepartie.
                Cotiser au compte permet aux particuliers de profiter de ces incitatifs,
                qui peuvent considérablement augmenter leur épargne pour l'achat d'une maison.
              </p>
            </div>

            {/* 2 */}
            <div className="bg-white text-gray-800 p-6 sm:p-8 rounded-3xl shadow-md">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Avantages fiscaux :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Cotiser à certains programmes d'épargne pour premiers acheteurs ou FHSA
                peut offrir des avantages fiscaux, tels que des déductions fiscales sur
                les cotisations ou des revenus de placement libres d'impôt. Ces avantages
                fiscaux peuvent constituer une incitation financière pour les particuliers
                à épargner en vue de leur première maison.
              </p>
            </div>

            {/* 3 */}
            <div className="bg-white text-gray-800 p-6 sm:p-8 rounded-3xl shadow-md">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Aide financière pour l'achat d'une maison :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                L'objectif principal des programmes FHSA est d'aider les particuliers à
                épargner pour leur première maison. En cotisant régulièrement, les
                participants constituent un fonds qui peut être utilisé pour des dépenses
                admissibles liées au logement, rendant l'accession à la propriété plus réalisable.
              </p>
            </div>

            {/* 4 */}
            <div className="bg-white text-gray-800 p-6 sm:p-8 rounded-3xl shadow-md">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Exigences relatives à l'objectif d'épargne :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Certains programmes FHSA peuvent exiger des participants qu'ils fixent et
                atteignent un objectif d'épargne dans un délai précis. Atteindre cet
                objectif peut donner droit aux incitatifs gouvernementaux ou à d'autres
                avantages, rendant les cotisations régulières nécessaires.
              </p>
            </div>

            {/* 5 */}
            <div className="bg-white text-gray-800 p-6 sm:p-8 rounded-3xl shadow-md">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Retraits limités pour l'achat d'une maison :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Les programmes FHSA comportent souvent des restrictions sur les retraits
                pour bénéficier de certains avantages. Cotiser et s'abstenir de retirer
                pour des dépenses non admissibles garantit que les particuliers peuvent
                accéder aux avantages maximaux au moment d'acheter une maison.
              </p>
            </div>

            {/* 6 */}
            <div className="bg-white text-gray-800 p-6 sm:p-8 rounded-3xl shadow-md">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Planification financière à long terme :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Cotiser à un FHSA fait partie d'une stratégie de planification financière
                à long terme. Cela aide les particuliers à épargner systématiquement en
                vue d'un objectif financier important — l'accession à la propriété —
                et garantit qu'ils disposent des fonds nécessaires au moment de l'achat.
              </p>
            </div>

            {/* 7 */}
            <div className="bg-white text-gray-800 p-6 sm:p-8 rounded-3xl shadow-md">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Éducation financière et counseling :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Certains programmes FHSA offrent des services d'éducation financière ou
                de counseling. S'engager avec ces ressources peut améliorer la littératie
                financière, aidant les particuliers à prendre des décisions éclairées
                concernant leur parcours d'achat immobilier.
              </p>
            </div>

            {/* 8 */}
            <div className="bg-white text-gray-800 p-6 sm:p-8 rounded-3xl shadow-md">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Constitution d'actifs :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Les cotisations régulières à un FHSA contribuent à la constitution
                d'actifs. À mesure que les particuliers épargnent et investissent,
                le compte croît avec le temps, constituant un actif précieux pouvant
                être utilisé pour la mise de fonds ou d'autres dépenses liées au logement.
              </p>
            </div>

            {/* 9 */}
            <div className="bg-white text-gray-800 p-6 sm:p-8 rounded-3xl shadow-md">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Accès à des taux d'intérêt plus bas :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Certains programmes FHSA peuvent offrir aux participants l'accès à des
                taux d'intérêt plus bas ou à des conditions hypothécaires plus favorables
                lorsqu'ils sont prêts à acheter une maison. Des cotisations régulières
                démontrent une responsabilité financière et un engagement envers
                l'accession à la propriété.
              </p>
            </div>

            {/* 10 */}
            <div className="bg-white text-gray-800 p-6 sm:p-8 rounded-3xl shadow-md">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Stabilité économique :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Les programmes FHSA visent à promouvoir la stabilité économique en
                encourageant une épargne responsable et l'accession à la propriété.
                Cotiser au compte s'aligne sur ces objectifs et peut conduire à une
                plus grande stabilité financière pour les particuliers et les familles.
              </p>
            </div>
          </div>

          <p className="text-base sm:text-lg text-gray-100 max-w-5xl mx-auto mt-12 leading-relaxed text-left sm:text-center">
            Avant de cotiser à un{" "}
            <span className="text-[#20a7df]">
              Compte d'épargne pour premiers acheteurs ou Compte d'épargne pour acheteurs immobiliers
            </span>
            , les particuliers doivent bien comprendre les modalités et conditions du
            programme spécifique auquel ils participent. Consulter des conseillers financiers chez{" "}
            <a
              href="/"
              rel="noopener noreferrer"
              className="text-[#20a7df] underline font-medium hover:opacity-90"
            >
              einsured.ca
            </a>{" "}
            et explorer les détails du FHSA garantira que les particuliers prennent des
            décisions éclairées pour maximiser les avantages du programme.
          </p>
          <div className="mt-10">
            <Link
              to="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
            >
              Commencer
            </Link>
          </div>
        </div>
      </section>

      {/* Reasons to Invest in an FHSA */}
      <section className="py-16 sm:py-20 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className={container}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-10">
            Raisons d'investir dans un FHSA
          </h2>

          <div className="space-y-6 sm:space-y-7 text-base sm:text-lg leading-relaxed max-w-5xl mx-auto">
            <div className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p>
                <span className="font-semibold text-[#49628b]">
                  Maximiser l'épargne :
                </span>{" "}
                Utilisez le FHSA pour épargner jusqu'à 40 000 $ spécifiquement
                destinés à l'achat de votre première maison.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p>
                <span className="font-semibold text-[#49628b]">
                  Cotisations libres d'impôt :
                </span>{" "}
                Profitez des avantages fiscaux en cotisant au FHSA en franchise d'impôt
                pendant jusqu'à 15 ans.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p>
                <span className="font-semibold text-[#49628b]">
                  Flexibilité de report :
                </span>{" "}
                Les droits de cotisation inutilisés ne sont pas perdus. Reportez
                tout montant inutilisé à l'année suivante, avec un report maximal de
                8 000 $.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p>
                <span className="font-semibold text-[#49628b]">
                  Efficacité fiscale :
                </span>{" "}
                Réduisez potentiellement votre facture fiscale globale en tirant parti
                des cotisations non déduites, et la possibilité de les reporter
                indéfiniment offre une flexibilité dans la gestion de vos obligations fiscales.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p>
                <span className="font-semibold text-[#49628b]">
                  Revenus libres d'impôt :
                </span>{" "}
                Bénéficiez de l'avantage de ne pas payer d'impôt sur les revenus de
                placement dans le FHSA, ce qui permet à votre épargne de croître
                plus efficacement.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p>
                <span className="font-semibold text-[#49628b]">
                  Complémentaire au RAP :
                </span>{" "}
                Le FHSA complète le Régime d'accession à la propriété (RAP), offrant
                une avenue supplémentaire pour épargner en vue de l'achat de votre première maison.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Is an FHSA right for me? */}
      <section className="py-16 sm:py-2 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className={`${container} text-center`}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b]">
            Un FHSA est-il fait pour moi ?
          </h2>

          <p className="mt-4 sm:mt-6 text-base sm:text-lg leading-relaxed max-w-5xl mx-auto">
            Envisagez le{" "}
            <span className="text-[#20a7df] font-semibold">
              Compte d'épargne pour l'achat d'une première propriété (FHSA)
            </span>{" "}
            si vous cherchez un outil d'épargne fiscalement avantageux et flexible pour
            accumuler des fonds pour votre première maison, avec l'avantage supplémentaire
            de compléter les plans d'achat immobilier existants comme le Régime
            d'accession à la propriété. Il est conseillé de consulter des experts
            financiers pour déterminer si un FHSA s'aligne sur vos objectifs et
            votre stratégie financière globale.
          </p>

          <h3 className="mt-10 sm:mt-20 text-2xl sm:text-3xl font-bold text-[#49628b]">
            Critères d'admissibilité pour ouvrir un FHSA
          </h3>

          <p className="mt-3 sm:mt-4 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
            Pour ouvrir un Compte d'épargne pour l'achat d'une première propriété, vous devez être :
          </p>

          <div className="mt-8 sm:mt-10 grid gap-4 sm:gap-6 md:grid-cols-3">
            <div className="bg-[#f5f9fc] border border-gray-200 rounded-3xl px-6 py-6 sm:px-8 sm:py-8 flex items-center gap-4 text-left shadow-sm">
              <CheckCircle
                aria-hidden="true"
                className="h-7 w-7 text-sky-400 shrink-0"
                strokeWidth={2.4}
              />
              <p className="font-semibold text-[#49628b] text-base sm:text-lg">
                Résident canadien
              </p>
            </div>

            <div className="bg-[#f5f9fc] border border-gray-200 rounded-3xl px-6 py-6 sm:px-8 sm:py-8 flex items-center gap-4 text-left shadow-sm">
              <CheckCircle
                aria-hidden="true"
                className="h-7 w-7 text-sky-400 shrink-0"
                strokeWidth={2.4}
              />
              <p className="font-semibold text-[#49628b] text-base sm:text-lg">
                Âgé de 18 ans ou plus
              </p>
            </div>

            <div className="bg-[#f5f9fc] border border-gray-200 rounded-3xl px-6 py-6 sm:px-8 sm:py-8 flex items-center gap-4 text-left shadow-sm">
              <CheckCircle
                aria-hidden="true"
                className="h-7 w-7 text-sky-400 shrink-0"
                strokeWidth={2.4}
              />
              <p className="font-semibold text-[#49628b] text-base sm:text-lg">
                Premier acheteur immobilier
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What is a qualifying home? */}
      <section className="py-16 sm:py-20 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className={container}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6">
            Qu'est-ce qu'une habitation admissible ?
          </h2>

          <p className="text-base sm:text-lg leading-relaxed max-w-5xl mx-auto mb-4">
            Une{" "}
            <a
              href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/first-home-savings-account.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-600 font-medium underline hover:opacity-90"
            >
              habitation admissible
            </a>{" "}
            est une unité d'habitation située au Canada. Cela comprend les maisons
            existantes et celles en construction.
          </p>

          <p className="text-base sm:text-lg leading-relaxed max-w-5xl mx-auto mb-8">
            Les maisons unifamiliales, semi-détachées, les maisons en rangée, les maisons
            mobiles, les unités de condominium et les appartements dans des duplex,
            triplex, quadruplex ou immeubles à appartements sont tous admissibles. Une
            part dans une coopérative de logement est également admissible si elle vous
            donne droit à la possession et vous confère un intérêt dans une unité
            d'habitation située au Canada.
          </p>

          <div className="flex justify-center mb-14">
            <Link
              to="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
            >
              Investir maintenant
            </Link>
          </div>

          <h3 className="text-2xl mt-18 sm:text-3xl font-bold text-[#49628b] text-center mb-4">
            Retraits du FHSA :
          </h3>

          <p className="text-base sm:text-lg leading-relaxed max-w-5xl mx-auto mb-3 text-center md:text-left">
            Les retraits du FHSA se divisent en deux catégories : les retraits admissibles et
            les retraits non admissibles. Pour effectuer un retrait libre d'impôt, souvent
            appelé « retrait admissible », certains critères doivent être respectés.
          </p>

          <p className="text-base sm:text-lg leading-relaxed max-w-5xl mx-auto mb-10 text-center md:text-left">
            Consultez{" "}
            <a
              href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/first-home-savings-account/withdrawals-transfers-out-your-fhsas.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-600 font-medium underline hover:opacity-90"
            >
              Canada.ca
            </a>{" "}
            pour connaître les critères de retrait du FHSA.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#f5f7fb] rounded-3xl px-6 sm:px-8 py-8 shadow-sm">
              <h4 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                Retrait admissible :
              </h4>
              <div className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0"
                  strokeWidth={2.4}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  Lorsque vous remplissez les conditions du gouvernement, vous avez la
                  flexibilité d'effectuer des retraits libres d'impôt de votre FHSA,
                  que ce soit en un seul retrait ou par une série de retraits. Il est
                  important de savoir que des règles spécifiques s'appliquent après
                  votre premier retrait admissible. Pour plus d'informations, veuillez
                  consulter l'un de nos conseillers.
                </p>
              </div>
            </div>

            <div className="bg-[#f5f7fb] rounded-3xl px-6 sm:px-8 py-8 shadow-sm">
              <h4 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                Retrait non admissible :
              </h4>
              <div className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0"
                  strokeWidth={2.4}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  Si vous ne pouvez pas remplir les conditions du gouvernement, vous avez
                  toujours la possibilité d'effectuer des retraits « non admissibles »
                  de votre FHSA. Il est important de noter que ces retraits sont assujettis
                  à une retenue d'impôt et seront considérés comme faisant partie de votre
                  revenu imposable dans l'année du retrait.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why should I open an FHSA account with einsured.ca ? */}
      <section className="relative bg-[#dce2ec] border-y-4 border-[#2b4672] py-16 sm:py-20 px-4 sm:px-6 md:px-20 text-gray-800">
        <div className={`${container} text-center`}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-10">
            Pourquoi ouvrir un compte FHSA avec einsured.ca ?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[#2b4672]/40 bg-[#dce2ec] rounded-lg overflow-hidden shadow-sm">
            <div className="px-6 sm:px-8 py-8 text-left">
              <h3 className="text-lg sm:text-xl font-bold text-[#49628b] mb-3">
                Plateforme conviviale
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Notre plateforme en ligne intuitive et conviviale vous permet de gérer
                et de surveiller facilement vos investissements dans un{" "}
                <span className="text-sky-600 font-semibold hover:opacity-90">
                  Régime enregistré d'épargne-retraite (REER)
                </span>{" "}
                . Grâce à une interface fluide, vous pouvez rester informé des tendances
                du marché, suivre la performance de votre portefeuille et prendre des
                décisions éclairées en toute simplicité.
              </p>
            </div>

            <div className="px-6 sm:px-8 py-8 text-left">
              <h3 className="text-lg sm:text-xl font-bold text-[#49628b] mb-3">
                Conseils d'experts
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                <a
                  href="/"
                  rel="noopener noreferrer"
                  className="text-sky-600 font-semibold underline hover:opacity-90"
                >
                  einsured.ca
                </a>{" "}
                s'engage à vous aider à prendre des décisions d'investissement éclairées.
                Notre équipe d'experts financiers est disponible pour vous guider,
                répondre à vos questions et offrir des conseils personnalisés pour
                aligner votre stratégie REER sur vos objectifs financiers uniques.
              </p>
            </div>

            <div className="px-6 sm:px-8 py-8 text-left">
              <h3 className="text-lg sm:text-xl font-bold text-[#49628b] mb-3">
                Outils innovants
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Accédez à des outils et ressources de pointe conçus pour vous aider
                à faire des choix d'investissement bien informés. Des calculateurs
                financiers aux études de marché, notre plateforme vous fournit les
                outils nécessaires pour optimiser votre portefeuille REER.
              </p>
            </div>

            <div className="px-6 sm:px-8 py-8 text-left">
              <h3 className="text-lg sm:text-xl font-bold text-[#49628b] mb-3">
                Sécurité et confiance
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                La sécurité est notre priorité absolue. Soyez assuré que vos
                investissements sont protégés par des mesures de sécurité de pointe.
                einsured.ca s'engage à maintenir les plus hauts standards de sécurité
                des données et de confidentialité.
              </p>
            </div>
          </div>

          <div className="mt-12 flex justify-center">
            <Link
              to="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
            >
              Commencer
            </Link>
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles de blogue" mode="related" relatedCount={3} />
    </>
  );
}
